import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './tooltip.css';

const TooltipContext = createContext(null);

const POSITION_STYLE_ID = 'mp-tooltip-position-rules';
const positionRuleCache = new Set();

function getPositionStyleSheet() {
  let styleTag = document.getElementById(POSITION_STYLE_ID);
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = POSITION_STYLE_ID;
    document.head.appendChild(styleTag);
  }
  return styleTag.sheet;
}

function getPositionClass(left, top) {
  const safeLeft = Math.round(left);
  const safeTop = Math.round(top);
  const className = `mp-tooltip-pos-${safeLeft}-${safeTop}`;

  if (!positionRuleCache.has(className)) {
    const sheet = getPositionStyleSheet();
    sheet.insertRule(`.${className}{left:${safeLeft}px;top:${safeTop}px;}`, sheet.cssRules.length);
    positionRuleCache.add(className);
  }

  return className;
}

function getPlacement(rect, preferredPlacement) {
  if (!rect) return preferredPlacement || 'top';

  const hasTopSpace = rect.top > 80;
  if (preferredPlacement === 'top' && !hasTopSpace) {
    return 'bottom';
  }

  return preferredPlacement || 'top';
}

function getCoordinates(rect, placement) {
  if (!rect) {
    return { left: 0, top: 0 };
  }

  const viewportWidth = window.innerWidth || 0;
  const baseLeft = rect.left + (rect.width / 2);
  const clampedLeft = Math.max(24, Math.min(baseLeft, viewportWidth - 24));

  if (placement === 'bottom') {
    return {
      left: clampedLeft,
      top: rect.bottom + 10,
    };
  }

  return {
    left: clampedLeft,
    top: rect.top - 10,
  };
}

function TooltipPortal({ activeTooltip, layerClassName }) {
  if (!activeTooltip || typeof document === 'undefined') {
    return null;
  }

  const iconMap = {
    info: 'ⓘ',
    warning: '⚠',
    tip: '✦',
  };

  const icon = activeTooltip.icon || iconMap[activeTooltip.variant] || null;

  return createPortal(
    <div className={`mp-tooltip-layer ${layerClassName || ''}`}>
      <div
        id={activeTooltip.id}
        role="tooltip"
        className={`mp-tooltip mp-tooltip--${activeTooltip.variant} ${activeTooltip.positionClass} ${activeTooltip.placement === 'bottom' ? 'is-bottom' : ''}`}
      >
        {icon ? <span className="mp-tooltip__icon" aria-hidden="true">{icon}</span> : null}
        <span className="mp-tooltip__text">{activeTooltip.content}</span>
      </div>
    </div>,
    document.body,
  );
}

export function TooltipProvider({
  children,
  delay = 300,
  mobileTap = true,
  layerClassName = 'mp-tooltip-layer--default',
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const delayTimerRef = useRef(null);

  const clearDelay = useCallback(() => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  }, []);

  const closeTooltip = useCallback((id) => {
    setActiveTooltip((current) => {
      if (!current) return null;
      if (!id || current.id === id) return null;
      return current;
    });
  }, []);

  const openTooltip = useCallback((payload) => {
    const {
      id,
      content,
      variant = 'info',
      icon,
      anchorRect,
      placement = 'top',
      customDelay,
    } = payload;

    if (!id || !content || !anchorRect) return;

    clearDelay();

    const nextDelay = typeof customDelay === 'number' ? customDelay : delay;
    delayTimerRef.current = setTimeout(() => {
      const nextPlacement = getPlacement(anchorRect, placement);
      const coords = getCoordinates(anchorRect, nextPlacement);
      const positionClass = getPositionClass(coords.left, coords.top);

      setActiveTooltip({
        id,
        content,
        variant,
        icon,
        placement: nextPlacement,
        positionClass,
      });
    }, Math.max(0, nextDelay));
  }, [clearDelay, delay]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeTooltip();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeTooltip]);

  useEffect(() => () => clearDelay(), [clearDelay]);

  const value = useMemo(() => ({
    delay,
    mobileTap,
    activeTooltip,
    openTooltip,
    closeTooltip,
  }), [activeTooltip, closeTooltip, delay, mobileTap, openTooltip]);

  return (
    <TooltipContext.Provider value={value}>
      {children}
      <TooltipPortal activeTooltip={activeTooltip} layerClassName={layerClassName} />
    </TooltipContext.Provider>
  );
}

export function useTooltipContext() {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('Tooltip components must be used inside TooltipProvider.');
  }
  return context;
}
